import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Hash,
  Loader2,
  Package,
  Phone,
  Search,
  ShieldAlert,
} from 'lucide-react';

import FloatingField from '../components/FloatingField';
import useToast from '../hooks/useToast';
import { API_URL } from '../config';
import { STAGES_CONFIG } from '../utils/stages';
import { haptic } from '../utils/vibration';

const TRACKING_LOCK_FALLBACK_MINUTES = 15;

const STATUS_TONE = {
  unassigned: {
    badge: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
    accent: 'from-slate-500 to-slate-700',
  },
  active: {
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200',
    accent: 'from-blue-500 via-cyan-500 to-indigo-600',
  },
  revision_active: {
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200',
    accent: 'from-amber-500 via-orange-500 to-rose-500',
  },
  completed: {
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200',
    accent: 'from-emerald-500 via-teal-500 to-cyan-500',
  },
  cancelled: {
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-200',
    accent: 'from-rose-500 via-red-500 to-orange-500',
  },
};

const STAGE_ACCENT_CLASSES = {
  orange: {
    completed: 'border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-300/40 dark:bg-emerald-400/20 dark:text-emerald-100',
    current: 'border-orange-100 bg-orange-300 text-slate-950 shadow-[0_10px_30px_rgba(251,146,60,0.40)]',
  },
  cyan: {
    completed: 'border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-300/40 dark:bg-emerald-400/20 dark:text-emerald-100',
    current: 'border-cyan-100 bg-cyan-300 text-slate-950 shadow-[0_10px_30px_rgba(34,211,238,0.40)]',
  },
  purple: {
    completed: 'border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-300/40 dark:bg-emerald-400/20 dark:text-emerald-100',
    current: 'border-purple-100 bg-purple-300 text-slate-950 shadow-[0_10px_30px_rgba(192,132,252,0.40)]',
  },
  yellow: {
    completed: 'border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-300/40 dark:bg-emerald-400/20 dark:text-emerald-100',
    current: 'border-yellow-100 bg-yellow-300 text-slate-950 shadow-[0_10px_30px_rgba(253,224,71,0.40)]',
  },
  green: {
    completed: 'border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-300/40 dark:bg-emerald-400/20 dark:text-emerald-100',
    current: 'border-green-100 bg-green-300 text-slate-950 shadow-[0_10px_30px_rgba(74,222,128,0.40)]',
  },
  emerald: {
    completed: 'border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-300/40 dark:bg-emerald-400/20 dark:text-emerald-100',
    current: 'border-emerald-100 bg-emerald-300 text-slate-950 shadow-[0_10px_30px_rgba(52,211,153,0.40)]',
  },
  blue: {
    completed: 'border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-300/40 dark:bg-emerald-400/20 dark:text-emerald-100',
    current: 'border-blue-100 bg-blue-300 text-slate-950 shadow-[0_10px_30px_rgba(96,165,250,0.40)]',
  },
};

const formatPhone = (value) => {
  let cleaned = String(value || '').replace(/\D/g, '');
  if (!cleaned.startsWith('7')) {
    cleaned = `7${cleaned}`;
  }
  if (cleaned.length > 11) {
    cleaned = cleaned.slice(0, 11);
  }

  let formatted = '+7';
  if (cleaned.length > 1) {
    formatted += ` (${cleaned.slice(1, 4)}`;
    if (cleaned.length > 4) {
      formatted += `) ${cleaned.slice(4, 7)}`;
      if (cleaned.length > 7) {
        formatted += `-${cleaned.slice(7, 9)}`;
        if (cleaned.length > 9) {
          formatted += `-${cleaned.slice(9, 11)}`;
        }
      }
    }
  }
  return formatted;
};

const normalizePhone = (value) => {
  let digits = String(value || '').replace(/\D/g, '');
  if (!digits.startsWith('7')) {
    digits = `7${digits}`;
  }
  return digits.slice(0, 11);
};

const formatOrderNumberInput = (value) => (
  String(value || '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trimStart()
);

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const extractErrorMessage = (payload, fallback) => {
  if (typeof payload?.detail === 'string' && payload.detail.trim()) return payload.detail;
  if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message;
  if (typeof payload?.detail?.message === 'string' && payload.detail.message.trim()) return payload.detail.message;
  return fallback;
};

const StatusTracker = ({ stages = [], animationKey = 0 }) => {
  const stageCount = stages.length;
  const completedCount = stages.filter((stage) => stage.state === 'completed').length;
  const currentIndex = stages.findIndex((stage) => stage.state === 'current');
  const progressRatio = useMemo(() => {
    if (stageCount <= 0) return 0;
    if (stageCount === 1) {
      return stages[0]?.state === 'pending' ? 0 : 1;
    }
    const base = currentIndex >= 0 ? completedCount + 0.5 : completedCount;
    return Math.max(0, Math.min(1, base / (stageCount - 1)));
  }, [completedCount, currentIndex, stageCount, stages]);

  if (!stageCount) {
    return (
      <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-5 text-center text-sm text-white/75 backdrop-blur-sm">
        Статус заказа обновляется.
      </div>
    );
  }

  const lineInset = stageCount > 1 ? `${50 / stageCount}%` : '50%';

  return (
    <div className="relative pt-2">
      <div
        className="absolute top-7 h-1 overflow-hidden rounded-full bg-white/20"
        style={{ left: lineInset, right: lineInset }}
      >
        <motion.div
          key={`progress-${animationKey}`}
          className="h-full rounded-full bg-gradient-to-r from-white/95 via-cyan-200 to-blue-100"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: progressRatio }}
          transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformOrigin: 'left center' }}
        />
      </div>

      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${stageCount}, minmax(0, 1fr))` }}
      >
        {stages.map((stage, index) => {
          const config = STAGES_CONFIG[stage.key] || {};
          const Icon = config.icon || Package;
          const isCurrent = stage.state === 'current';
          const isCompleted = stage.state === 'completed';
          const accent = STAGE_ACCENT_CLASSES[config.color] || STAGE_ACCENT_CLASSES.blue;

          return (
            <div key={stage.key} className="relative flex flex-col items-center text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.72, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{
                  delay: 0.2 + index * 0.14,
                  duration: 0.48,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={[
                  'relative z-10 flex h-10 w-10 items-center justify-center rounded-full border text-white shadow-lg',
                  isCurrent
                    ? accent.current
                    : isCompleted
                    ? accent.completed
                    : 'border-white/20 bg-white/10 text-white/70 backdrop-blur-sm',
                ].join(' ')}
              >
                {isCurrent && (
                  <motion.span
                    className="absolute inset-0 rounded-full border border-cyan-100/80"
                    initial={{ scale: 1, opacity: 0.7 }}
                    animate={{ scale: 1.45, opacity: 0 }}
                    transition={{ duration: 1.9, repeat: Infinity, ease: 'easeOut' }}
                  />
                )}
                <Icon className="h-[18px] w-[18px]" strokeWidth={2.25} />
              </motion.div>
              <div className="mt-3 px-1 text-[10px] font-medium leading-[1.25] text-white/90">
                {stage.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TrackOrder = ({ embedded = false }) => {
  const [phone, setPhone] = useState('+7');
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);
  const [blockedInfo, setBlockedInfo] = useState(null);
  const [animationSeed, setAnimationSeed] = useState(0);
  const { toast, showToast, hideToast } = useToast(3500);

  const normalizedPhone = normalizePhone(phone);
  const canSubmit = normalizedPhone.length === 11 && orderNumber.trim().length > 0 && !loading;
  const tone = STATUS_TONE[trackingResult?.lifecycle_status] || STATUS_TONE.active;

  const handleSubmit = async (event) => {
    event.preventDefault();
    haptic('selection');

    if (normalizedPhone.length !== 11 || !orderNumber.trim()) {
      haptic('info');
      showToast('Введите телефон и номер заказа', 'info');
      return;
    }

    setLoading(true);
    setBlockedInfo(null);
    setTrackingResult(null);

    try {
      const response = await fetch(`${API_URL}/api/public/order-tracking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: normalizedPhone,
          order_number: orderNumber.trim(),
        }),
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      if (!response.ok) {
        if (response.status === 429) {
          const remainingMinutes = Number(payload?.detail?.remaining_minutes || TRACKING_LOCK_FALLBACK_MINUTES);
          const blockedUntil = payload?.detail?.blocked_until || null;
          const message = extractErrorMessage(payload, `Слишком много попыток. Повторите через ${remainingMinutes} мин.`);
          setBlockedInfo({
            message,
            remainingMinutes,
            blockedUntil,
          });
          haptic('warning');
          showToast(message, 'warning');
          return;
        }

        const message = extractErrorMessage(payload, 'Не удалось проверить заказ');
        haptic('error');
        showToast(message, 'error');
        return;
      }

      setTrackingResult(payload);
      setAnimationSeed((prev) => prev + 1);
      haptic('success');
    } catch (error) {
      haptic('error');
      showToast('Нет ответа от сервера. Попробуйте позже.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const wrapperClassName = embedded
    ? 'mx-auto w-full max-w-4xl space-y-4'
    : 'min-h-screen bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.10),_transparent_32%),linear-gradient(180deg,#f8fbff_0%,#eef4ff_48%,#f8fafc_100%)] px-4 py-6 dark:bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.18),_transparent_32%),linear-gradient(180deg,#020617_0%,#0f172a_58%,#111827_100%)]';

  const contentClassName = embedded
    ? 'space-y-4'
    : 'mx-auto flex w-full max-w-2xl flex-col gap-4';

  return (
    <div className={wrapperClassName}>
      <div className={contentClassName}>
        {!embedded && (
          <div className="px-1 pt-2">
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
              Узнать статус заказа
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Введите номер телефона и номер заказа.
            </p>
          </div>
        )}

        <div className="rounded-[28px] border border-white/70 bg-white/90 p-5 shadow-[0_24px_60px_rgba(15,23,42,0.10)] backdrop-blur dark:border-slate-800 dark:bg-slate-900/90 dark:shadow-[0_24px_60px_rgba(0,0,0,0.35)] sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FloatingField label="Телефон">
                <div className="relative">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(event) => setPhone(formatPhone(event.target.value))}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-11 text-[15px] font-medium text-slate-950 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-blue-500 dark:focus:ring-blue-950/70"
                    placeholder="+7 (999) 123-45-67"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                  <Phone className="pointer-events-none absolute right-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                </div>
              </FloatingField>

              <FloatingField label="Номер заказа">
                <div className="relative">
                  <input
                    type="text"
                    value={orderNumber}
                    onChange={(event) => setOrderNumber(formatOrderNumberInput(event.target.value))}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-11 text-[15px] font-medium uppercase tracking-[0.02em] text-slate-950 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-blue-500 dark:focus:ring-blue-950/70"
                    placeholder="Введите номер заказа"
                    autoCapitalize="characters"
                    autoComplete="off"
                  />
                  <Hash className="pointer-events-none absolute right-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                </div>
              </FloatingField>
            </div>

            {blockedInfo && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-200">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="mt-0.5 h-[18px] w-[18px] shrink-0" />
                  <div>
                    <div className="font-medium">{blockedInfo.message}</div>
                    {blockedInfo.blockedUntil && (
                      <div className="mt-1 text-xs text-amber-700/80 dark:text-amber-200/80">
                        Повторная попытка будет доступна после {formatDateTime(blockedInfo.blockedUntil)}.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(37,99,235,0.28)] transition hover:shadow-[0_18px_40px_rgba(37,99,235,0.34)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Search className="h-[18px] w-[18px]" />}
              {loading ? 'Проверяем...' : 'Проверить статус'}
            </button>
          </form>
        </div>

        {trackingResult && (
          <motion.section
            key={`${trackingResult.order_number}-${trackingResult.current_stage || trackingResult.lifecycle_status}-${animationSeed}`}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden rounded-[30px] border border-white/70 bg-white/90 shadow-[0_24px_60px_rgba(15,23,42,0.12)] backdrop-blur dark:border-slate-800 dark:bg-slate-900/90 dark:shadow-[0_24px_60px_rgba(0,0,0,0.38)]"
          >
            <div className={`bg-gradient-to-r ${tone.accent} px-5 py-5 text-white sm:px-6 sm:py-6`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-medium uppercase tracking-[0.18em] text-white/70">Заказ</div>
                  <div className="mt-1 text-2xl font-semibold tracking-tight">
                    {trackingResult.order_number}
                  </div>
                </div>
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${tone.badge}`}>
                  {trackingResult.lifecycle_label}
                </span>
              </div>

              <div className="mt-5 rounded-[26px] border border-white/20 bg-white/10 px-4 py-5 backdrop-blur-sm">
                <StatusTracker stages={trackingResult.stage_sequence || []} animationKey={animationSeed} />
              </div>
            </div>

            <div className="grid gap-3 px-5 py-5 sm:grid-cols-2 sm:px-6 sm:py-6">
              <div className="rounded-2xl bg-slate-50 px-4 py-4 dark:bg-slate-950/80">
                <div className="text-xs font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Текущий этап</div>
                <div className="mt-2 text-base font-semibold text-slate-950 dark:text-white">
                  {trackingResult.current_stage_label || trackingResult.lifecycle_label}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 px-4 py-4 dark:bg-slate-950/80">
                <div className="text-xs font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  {trackingResult.completed_date ? 'Завершён' : 'Оформлен'}
                </div>
                <div className="mt-2 text-base font-semibold text-slate-950 dark:text-white">
                  {formatDateTime(trackingResult.completed_date || trackingResult.created_date)}
                </div>
              </div>
            </div>
          </motion.section>
        )}
      </div>

          </div>
  );
};

export default TrackOrder;
